var __assign = (this && this.__assign) || Object.assign || function(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
        s = arguments[i];
        for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
            t[p] = s[p];
    }
    return t;
};
import * as Long from "long";
import { Point } from "pixi.js";
import { ShopCategoryType } from "../features/balancing/types/BalancingCore";
import { selectBuildingStageInfo } from "../selectors/selectExistingBuildingInfo";
import { utilGrid } from "./grid/utilGrid";
import { BuildingStateType } from "./network/generated/protoTypes";
import { utilNumber } from "./utilNumber";
import { utilTime } from "./utilTime";
var MAX_PRODUCTION_STEPS = 4;
var CONTEXT_MENU_HORIZONTAL_BUFFER = 100;
var UtilBuildings = /** @class */ (function () {
    function UtilBuildings() {
        var _this = this;
        this.buildingDataToGridObject = function (data) { return ({
            id: data.proto.id,
            volume: data.proto.stage,
            x: data.proto.posX,
            xExtent: data.proto.flipped ? data.balancing.yExtent : data.balancing.xExtent,
            y: data.proto.posY,
            yExtent: data.proto.flipped ? data.balancing.xExtent : data.balancing.yExtent,
        }); };
        this.topologicalSortBuildings = function (buildings) {
            var objects = buildings.map(_this.buildingDataToGridObject);
            return utilGrid.topologicalSort(objects);
        };
        this.getCenterPositionOnScreen = function (building, viewport, buildingHeight) {
            if (buildingHeight === void 0) { buildingHeight = 0; }
            var gridObj = __assign({}, _this.buildingDataToGridObject(building), { volume: buildingHeight });
            var p = utilGrid.objectCenterOnScreenGlobal(gridObj);
            return viewport.toGlobal(new Point(p.x, p.y));
        };
        this.getCenterPositionOnMap = function (building) {
            var gridObj = _this.buildingDataToGridObject(building);
            return utilGrid.mapToScreen(gridObj.xExtent * 0.5, gridObj.yExtent * 0.5);
        };
        this.getHoverPositionOnMap = function (building, height) {
            if (height === void 0) { height = 6; }
            var gridObj = __assign({}, _this.buildingDataToGridObject(building), { volume: height });
            var pos = utilGrid.objectHoverOnMap(gridObj);
            return utilGrid.mapToScreen(pos.x, pos.y);
        };
        this.getBuildingDimensions = function (building) {
            var obj = _this.buildingDataToGridObject(building);
            var _a = utilGrid.getDimensions(obj), width = _a.width, height = _a.height;
            return {
                height: height * 0.6,
                width: width * 0.6,
            };
        };
        this.getHoverPositionOnScreen = function (building, viewport) {
            return _this.getCenterPositionOnScreen(building, viewport, 5);
        };
        this.getRootPositionOnScreen = function (building, viewport) {
            var gridObj = _this.buildingDataToGridObject(building);
            var p = utilGrid.mapToScreenGlobal(gridObj.x, gridObj.y);
            return viewport.toGlobal(new Point(p.x, p.y));
        };
        this.getCenterPositionMinSquareGlobal = function (building) {
            var gridObj = _this.buildingDataToGridObject(building);
            return utilGrid.objectCenterOnScreenGlobal(gridObj);
        };
        this.isNotProducing = function (building) { return building.production === undefined; };
        this.isProducing = function (building) {
            return !!(building.production !== undefined &&
                building.production.production &&
                building.production.production.remainingTime);
        };
        this.hasFinishedProduction = function (building, removedProductions) {
            if (building.production != undefined) {
                var remainingTime = building.production.production.remainingTime;
                return remainingTime == undefined || utilTime.secondsLeftTill(remainingTime) < 0;
            }
            else if (removedProductions && typeof removedProductions !== "number") {
                return removedProductions.some(function (prod) { return prod.buildingId === building.proto.id; });
            }
            return false;
        };
        this.getProductionProgressPercent = function (building) {
            var production = building.production;
            if (production != undefined) {
                var totalTime = production.recipe.productionTime || 0;
                var timeLeft = utilTime.secondsLeftTill(production.production.remainingTime || 1);
                return utilTime.timerProgressReversed(timeLeft, totalTime);
            }
            return -1;
        };
        this.getProductionCurrentStep = function (building, stepCount) {
            if (stepCount === void 0) { stepCount = MAX_PRODUCTION_STEPS; }
            var percent = _this.getProductionProgressPercent(building);
            if (percent === -1) {
                return -1;
            }
            var step = utilNumber.clamp(Math.floor(percent / (100 / stepCount)), 0, stepCount - 1);
            if (step >= stepCount - 1 && percent !== 100) {
                step--;
            }
            return step;
        };
        this.hasResourceProduct = function (building, resourceId) {
            return _this.isNotProducing(building) ||
                building.production.recipe.product.some(function (productInfo) { return productInfo.resourceId === resourceId; });
        };
        this.isIdle = function (building) { return building.proto.state === undefined; };
        this.isUnderConstruction = function (building) {
            return building.proto.remainingTime != undefined &&
                (building.proto.state === BuildingStateType.CONSTRUCTING ||
                    building.proto.state === BuildingStateType.UPGRADING);
        };
        this.hasFinishedConstruction = function (building) {
            return !building.proto.remainingTime &&
                (building.proto.state === BuildingStateType.CONSTRUCTING ||
                    building.proto.state === BuildingStateType.UPGRADING);
        };
        this.shouldPlayCelebrateAnimation = function (buildingState, preBuildingState) {
            return buildingState === BuildingStateType.IDLE &&
                (preBuildingState === BuildingStateType.CONSTRUCTING ||
                    preBuildingState === BuildingStateType.UPGRADING);
        };
        this.isPremium = function (building) { return !!building.balancing.isPremium; };
        this.getVisualLevel = function (building) {
            var proto = building.proto, balancing = building.balancing;
            var stageId = proto.stage || 1;
            var stage = balancing.stage[stageId - 1];
            return stage.visualLevel || stage.level || 1;
        };
        this.getPreviousProtoStage = function (building, minValue) {
            if (minValue === void 0) { minValue = 0; }
            var foundStage = _this.getProtoStage(building, minValue);
            return foundStage <= minValue ? minValue : foundStage - 1;
        };
        this.getNextProtoStage = function (building, maxValue) {
            if (maxValue === void 0) { maxValue = 0; }
            var foundStage = _this.getProtoStage(building, maxValue);
            return foundStage <= maxValue ? maxValue : foundStage + 1;
        };
        this.getProtoStage = function (building, defaultValue) {
            if (defaultValue === void 0) { defaultValue = 0; }
            return (building.proto ? building.proto.stage || defaultValue : defaultValue);
        };
        this.getBalancingStage = function (building) {
            return building.balancing.stage[utilBuildings.getPreviousProtoStage(building)] ||
                building.balancing.stage[0];
        };
        this.isAcre = function (building) {
            return building.balancing.shopCategory === ShopCategoryType.Acres ||
                building.balancing.shopCategory === ShopCategoryType.AdvAcres;
        };
        this.isWindmill = function (building) {
            return building.balancing.shopCategory === ShopCategoryType.Windmills;
        };
        this.isSilo = function (building) {
            return building.balancing.shopCategory === ShopCategoryType.Silos;
        };
        this.sortBuildingsPreCalloutRender = function (buildings, viewport) {
            return buildings.slice().sort(function (a, b) {
                var aCalloutPosition = utilBuildings.getHoverPositionOnScreen(a, viewport);
                var bCalloutPosition = utilBuildings.getHoverPositionOnScreen(b, viewport);
                return bCalloutPosition.y === aCalloutPosition.y
                    ? aCalloutPosition.x - bCalloutPosition.x
                    : aCalloutPosition.y - bCalloutPosition.y;
            });
        };
        this.getAutoPlacementPosition = function (building, baseSetup, unlockedExpansionIds, existingBuildings) {
            var size = {
                x: building.xExtent,
                y: building.yExtent,
            };
            var occupiedTiles = _this.buildingsToOccupiedTileIds(existingBuildings, baseSetup);
            return utilGrid.getAutoPlacementPosition(size, baseSetup, unlockedExpansionIds, occupiedTiles);
        };
        this.buildingsToOccupiedTileIds = function (buildings, baseSetup) {
            var existingBuildings = buildings.map(_this.buildingDataToGridObject);
            return utilGrid.objectsToTileIds(existingBuildings, baseSetup);
        };
        this.getBuildingContextMenuHoverXPoint = function (hoverPositionX, contextMenuWidth) {
            return hoverPositionX - contextMenuWidth / 2 < 0
                ? contextMenuWidth / 2
                : hoverPositionX + contextMenuWidth / 2 > window.innerWidth
                    ? window.innerWidth - contextMenuWidth / 2
                    : hoverPositionX;
        };
        this.getBuildingContextMenuShouldClose = function (hoverPositionX, buildingPosX, buildingWidth) {
            return (hoverPositionX < buildingPosX - buildingWidth + CONTEXT_MENU_HORIZONTAL_BUFFER ||
                hoverPositionX - window.innerWidth + CONTEXT_MENU_HORIZONTAL_BUFFER >
                    buildingWidth - buildingPosX);
        };
        this.getWorkersDifference = function (building) {
            var stage = building.proto.stage || 0;
            var buildingInfo = selectBuildingStageInfo(building, stage);
            var workers = Long.fromValue(buildingInfo.workers || 0);
            if (stage > 0) {
                var previousStageBuildingInfo = selectBuildingStageInfo(building, stage - 1);
                return workers.subtract(Long.fromValue(previousStageBuildingInfo.workers || 0));
            }
            return workers;
        };
    }
    return UtilBuildings;
}());
export var utilBuildings = new UtilBuildings();



//////////////////
// WEBPACK FOOTER
// ./src/utils/utilBuildings.ts
// module id = 17
// module chunks = 3